import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { courseService } from '../services/api';
import { Course } from '../types/course';

export default function CoursesPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await courseService.getAllCourses();
        setCourses(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-golf-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">Error Loading Courses</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white px-6 py-2 rounded-lg transition-all duration-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">Golf Courses</h1>
            <p className="text-gray-600 dark:text-gray-400">Browse and manage golf courses</p>
          </div>
          <Link to="/courses/contribute" className="ml-auto mr-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-lg font-medium">
            Contribute scorecard
          </Link>
          <Link 
            to="/courses/new"
            className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            ➕ Add Course
          </Link>
        </div>

        {courses.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🏌️</div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">No Courses Yet</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Get started by adding your first golf course
            </p>
            <Link 
              to="/courses/new"
              className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 inline-block shadow-lg hover:shadow-xl"
            >
              Add Your First Course
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Link key={course.id} to={`/courses/${course.id}`}>
                <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-1">
                        {course.name}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 flex items-center">
                        📍 {course.location}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="bg-gradient-to-r from-golf-100 to-emerald-100 dark:from-golf-900 dark:to-emerald-900 px-3 py-1 rounded-full">
                        <span className="text-sm font-medium text-golf-800 dark:text-golf-200">
                          Par {course.par}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t dark:border-gray-700 pt-4">
                    <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
                      <span>{course.holes.length} holes</span>
                      <span>Added {new Date(course.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-8">
          <Link 
            to="/"
            className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-medium"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
